import { createWithEqualityFn } from 'zustand/traditional';
import { devtools } from 'zustand/middleware';
import axios, { AxiosResponse } from 'axios';
import { shallow } from 'zustand/shallow';
import {
  IDataTable,
  IDataTableCreatePayload,
} from '@shared/models/data-table.model';
import { getItemById } from '@shared/helpers/array.helpers';
import { updateDataTable } from '@shared/helpers/data-tables';
import { Replace } from '@shared/models/type-helpers.model';
import {
  IStoreState,
  UpdateClientAction,
} from '../../../src/store/store.model';
import { updateResourceLocally } from '../../../src/store/store.helpers';
import { isEqual } from 'lodash';

const STORE_NAME = 'data-tables';

export type IDataTableState = Replace<
  IStoreState<IDataTable>,
  {
    create: (payload: IDataTableCreatePayload) => Promise<IDataTable>;
  }
>;

const useDataTableStore = createWithEqualityFn<IDataTableState>()(
  devtools(
    (set, get) => ({
      isLoading: false,
      isUpdating: false,
      isDeleting: false,
      isCreating: false,
      error: undefined,
      // ...getHmrStoreState(STORE_NAME),

      updateLocally: ({ action, itemOrId, options = {} }) => {
        let { items } = get();

        if (
          action === UpdateClientAction.Add ||
          action === UpdateClientAction.Update
        ) {
          const { collection } = updateResourceLocally<IDataTable>({
            action,
            itemOrId,
            collection: items,
          });

          items = collection ?? items;
        }

        if (action === UpdateClientAction.Remove) {
          const { collection } = updateResourceLocally<IDataTable>({
            action,
            itemOrId,
            collection: items,
          });

          items = collection ?? items;
        }

        set(() => ({
          ...options,
          items,
        }));
      },

      fetch: async (organization: string) => {
        set({ isLoading: true, error: undefined });
        try {
          const { data: items } = await axios.get<IDataTable[]>('data-tables', {
            headers: { Organization: organization },
          });
          set({ items, isLoading: false });
          return items;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Unknown error',
            isLoading: false,
          });
          throw error;
        }
      },

      create: async (itemProps) => {
        set({ isCreating: true, error: undefined });
        try {
          const { data: newDataTable } = await axios.post<
            IDataTableCreatePayload,
            AxiosResponse<IDataTable>
          >('data-tables', itemProps);
          get().updateLocally({
            action: UpdateClientAction.Add,
            itemOrId: newDataTable,
            options: { isCreating: false },
          });
          return newDataTable;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Unknown error',
            isCreating: false,
          });
          throw error;
        }
      },

      update: async (changes) => {
        const { items = [] } = get();

        const { id } = changes;
        const prevDataTable = getItemById(
          items,
          id,
          'Could not find data table to update',
        );
        const nextDataTable = updateDataTable(prevDataTable, changes);

        // Don't update if the data table have not changed
        if (isEqual(prevDataTable, nextDataTable)) {
          return prevDataTable;
        }

        set({ isUpdating: true, error: undefined });
        try {
          const { data: serverDataTable } = await axios.put<IDataTable>(
            `data-tables/${id}`,
            nextDataTable,
          );
          get().updateLocally({
            action: UpdateClientAction.Update,
            itemOrId: serverDataTable,
            options: { isUpdating: false },
          });
          return serverDataTable;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Unknown error',
            isUpdating: false,
          });
          throw error;
        }
      },

      delete: async (id) => {
        set({ isDeleting: true, error: undefined });
        try {
          const { data: deletedDataTable } = await axios.delete<IDataTable>(
            `data-tables/${id}`,
          );
          const { items = [] } = get();
          const prevDataTable = items.find((table) => table.id === id);
          const nextDataTable =
            deletedDataTable ??
            (prevDataTable
              ? { ...prevDataTable, deleted_at: new Date().toISOString() }
              : undefined);

          if (!nextDataTable) {
            throw new Error(`Data table ${id} not found after delete`);
          }

          get().updateLocally({
            action: UpdateClientAction.Update,
            itemOrId: nextDataTable,
            options: { isDeleting: false },
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Unknown error',
            isDeleting: false,
          });
          throw error;
        }
      },
    }),
    { name: STORE_NAME },
  ),
  shallow,
);

// Initialize HMR store
// initHmrStore(STORE_NAME, useDataTableStore);

export { useDataTableStore };
